import { use, useMemo } from 'react'

import config from '@app/config'
import { ChatContext } from '@app/context'
import type { ConversationItem, MessageAttachment } from '@app/models'
import { DEFAULT_LANGUAGE, getPortalUrlByCulture } from '@app/utils/regional'
import { getEntitySlug } from '@app/utils/regional/getEntitySlug'

import { useListingCard } from '@lib/components'

import { isListingAttachment, normalizeUrl } from './constants'
import type { ListingAttachmentItem } from './types'
import { useAttachmentListingsContext } from './useAttachmentListingsContext'
import { useMainBuyerLocale } from '../../../Reply/useMainBuyerLocale'

export type UseListingAttachmentsParams = {
  attachments?: MessageAttachment[]
  conversation: ConversationItem
  listingIdsFromLinks?: number[]
}

export const useListingAttachments = ({
  attachments,
  conversation,
  listingIdsFromLinks,
}: UseListingAttachmentsParams): ListingAttachmentItem[] => {
  const { state } = use(ChatContext)
  const { fetchedListings } = useAttachmentListingsContext()
  const mainBuyerLocale = useMainBuyerLocale(conversation)
  const buyerCulture = mainBuyerLocale.culture
  const buyerLanguage = mainBuyerLocale.language
  const { getListingCardData } = useListingCard()

  const normalizedBaseUrl = useMemo(() => {
    const preferredCulture = buyerCulture ?? buyerLanguage
    const fallback =
      config.variables.properstarLink ?? 'https://www.properstar.com'

    const portalBaseUrl =
      (preferredCulture && getPortalUrlByCulture(preferredCulture)) ||
      getPortalUrlByCulture(DEFAULT_LANGUAGE) ||
      fallback

    return normalizeUrl(portalBaseUrl || fallback)
  }, [buyerCulture, buyerLanguage])

  const listingAttachments = useMemo(
    () => attachments?.filter(isListingAttachment) ?? [],
    [attachments]
  )

  const listingIds = useMemo(() => {
    if (!listingAttachments.length && !listingIdsFromLinks?.length) {
      return []
    }

    const attachmentIds = listingAttachments.map(
      (attachment) => attachment.listingId
    )
    const extractedIds = listingIdsFromLinks ?? []

    return Array.from(new Set<number>([...attachmentIds, ...extractedIds]))
  }, [listingAttachments, listingIdsFromLinks])

  const listingSlug = useMemo(
    () => getEntitySlug('listing', buyerCulture, buyerLanguage),
    [buyerCulture, buyerLanguage]
  )

  const projectSlug = useMemo(
    () => getEntitySlug('project', buyerCulture, buyerLanguage),
    [buyerCulture, buyerLanguage]
  )

  const listingAttachmentItems = useMemo(() => {
    if (!listingIds.length || !normalizedBaseUrl) {
      return [] as ListingAttachmentItem[]
    }

    return listingIds
      .map((listingId) => {
        const listing =
          state.listings.get(listingId) ?? fetchedListings.get(listingId)

        if (!listing) {
          return null
        }

        const cardData = getListingCardData(listing)
        const isProjectListing =
          listing.class === 'ProjectListing' ||
          listing.class === 'ProjectAsListing'

        const entitySlug = isProjectListing ? projectSlug : listingSlug
        const listingLink = `${normalizedBaseUrl}/${entitySlug}/${listingId}`

        return {
          id: listingId,
          cardData,
          listingLink,
        } satisfies ListingAttachmentItem
      })
      .filter((item): item is ListingAttachmentItem => item !== null)
  }, [
    fetchedListings,
    getListingCardData,
    listingSlug,
    normalizedBaseUrl,
    listingIds,
    projectSlug,
    state.listings,
  ])

  return listingAttachmentItems
}
